import React, { useRef } from 'react';
import { AImage } from '../../atoms/AImage/index';
import { ASubheader } from '../../atoms/ASubheader/index';
import { ABody } from '../../atoms/ABody/index';
import { AIcon } from '../../atoms/AIcon/index';
import { AIconSize } from '../../atoms/AIcon/AIcon.types';
import { ASubheaderSize } from '../../atoms/ASubheader/ASubheader.types';
import { ABodySize, ABodyColor } from '../../atoms/ABody/ABody.types';
import { useTracking } from '../../hooks';
import { DEFAULT_OPEN_IN_NEW_TAB_TEXT } from '../../utils/i18nConstants';
import type { OCardMiniImageProps } from './OCardMiniImage.types';
import * as Styled from './OCardMiniImage.styles';

const COMPONENT_NAME = 'o-mi-web-ui-kit-OCardMiniImage';
const TEST_ID = 'web-ui-kit-OCardMiniImage';

const ICON_INTERNAL = 'icon-arrow-right';
const ICON_EXTERNAL = 'icon-external-arrow';

export const OCardMiniImage: React.FC<OCardMiniImageProps> = ({
  aImage,
  aSubheader,
  aBody,
  ctaLink,
  externalLink = false,
  trackingProperties,
  custom_click_track_value,
  preTrackVal,
  className,
  'aria-label': ariaLabel,
  ...rest
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const linkRef = useRef<HTMLAnchorElement>(null);

  const labelText = aSubheader?.text ?? '';
  const isExternal = externalLink === true || externalLink === 'true';

  useTracking({
    ref: linkRef as React.RefObject<HTMLElement>,
    trackingProperties,
    isExternal,
    custom_click_track_value,
    preTrackVal,
    text: labelText,
  });

  const cardContent = (
    <>
      {aImage && (
        <Styled.ImageWrapper>
          <AImage {...aImage} />
        </Styled.ImageWrapper>
      )}
      <Styled.ContentWrapper>
        <Styled.TextWrapper>
          {aSubheader && (
            <Styled.HeaderSlot $hasBody={!!aBody}>
              <ASubheader
                text={aSubheader.text}
                element={aSubheader.element}
                size={aSubheader.size ?? ASubheaderSize.L}
              />
            </Styled.HeaderSlot>
          )}
          {aBody && (
            <Styled.BodySlot>
              <ABody
                bodyText={aBody.bodyText}
                size={aBody.size ?? ABodySize.S}
                color={aBody.color ?? ABodyColor.Default}
              />
            </Styled.BodySlot>
          )}
        </Styled.TextWrapper>
        {ctaLink && (
          <Styled.IconWrapper aria-hidden="true">
            <AIcon icon={isExternal ? ICON_EXTERNAL : ICON_INTERNAL} size={AIconSize.L} />
          </Styled.IconWrapper>
        )}
      </Styled.ContentWrapper>
    </>
  );

  return (
    <Styled.Wrapper
      ref={cardRef}
      className={className}
      $isLinked={!!ctaLink}
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      {...rest}
    >
      {ctaLink ? (
        <Styled.LinkWrapper
          ref={linkRef}
          href={ctaLink}
          aria-label={ariaLabel || labelText || undefined}
          {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {cardContent}
          {isExternal && <span className="sr-only">{DEFAULT_OPEN_IN_NEW_TAB_TEXT}</span>}
        </Styled.LinkWrapper>
      ) : (
        cardContent
      )}
    </Styled.Wrapper>
  );
};

export type { OCardMiniImageProps };
